import React, { useEffect, useMemo, useReducer, useState } from "react";
import Pagination from "../../components/Pagination";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { colors } from "../../config/theme";
import DeleteIcon from "../../components/icons/DeleteIcon";
import BoxWrapper from "../../components/BoxWrapper";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { general } from "../../locales/general";
import { CModal, CSpinner } from "@coreui/react";
import RenderTable from "../../components/RenderTable";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { useCitiesQuery } from "../../hooks/Cities/useCitiesQuery";
import CityTable from "./CityTable";
import AdminRoute from "../auth/RestrictedRoute";
import { CitiesSvg } from "../../components/SideBarMenu";
import NewCategory from "./NewCity";

const PAGE_SIZE = 10;

export default function Cities() {
  const [currentPage, setCurrentPage] = useState(1);
  const [visible, setVisible] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [searchQuery, setSearchQuery] = useState("");

  const isActiveParam =
    statusFilter === "all" ? undefined : statusFilter === "active";

  const { Cities, fetchingCities, deleteCity, mutating } =
    useCitiesQuery(isActiveParam);

  const filteredCities = useMemo(() => {
    if (!Cities) return [];
    return Cities.filter((city: any) =>
      city.label?.toLowerCase()?.includes(searchQuery.toLowerCase()),
    );
  }, [Cities, searchQuery]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredCities.length / PAGE_SIZE));
  }, [filteredCities]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const paginatedContent = usePaginatedContent(
    currentPage,
    PAGE_SIZE,
    filteredCities || [],
  );

  const [CitiesToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  const handleBulkDelete = () => {
    CitiesToBeDeleted.ids.forEach((id) => {
      if (!id) return;
      deleteCity(id);
      CitiesToBeDeleted.ids = [];
    });
  };

  return (
    <AdminRoute>
      <h5 className="fs-4 fw-bold mb-2">Villes</h5>
      <BoxWrapper>
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4 ">
          <div className="d-flex align-items-center gap-2 p-3">
            <button
              onClick={() => setVisible(!visible)}
              className="btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary"
            >
              + Ajouter une ville
            </button>
            <CModal
              alignment="center"
              visible={visible}
              onClose={() => setVisible(false)}
            >
              <NewCategory
                onAccept={() => {}}
                onClose={() => setVisible(false)}
              />
            </CModal>
            <button
              disabled={mutating || CitiesToBeDeleted?.ids.length === 0}
              onClick={handleBulkDelete}
              className="btn btn-outline-danger rounded-pill text-danger d-flex align-items-center gap-2 justify-content-center shadow-primary border"
            >
              {!mutating ? (
                <>
                  <DeleteIcon fill={colors.primary} /> Supprimer les villes
                </>
              ) : (
                <>
                  <CSpinner size="sm" /> {general.fr.wait}
                </>
              )}
            </button>
          </div>

          {/* Center: Search */}
          <div className="mt-3">
            <div className="search-form">
              <div className="input-field">
                <MagnifyingGlassIcon color="gray" width={20} />
                <input
                  type="text"
                  className="w-50 border-0 outline-none rounded-sm p-1 text-gray fs-6"
                  placeholder="Rechercher"
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 mt-2">
            <label className="fs-6 fw-bold text-gray-600 mb-0 mt-2">
              Statut :
            </label>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto", minWidth: "130px" }}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as "all" | "active" | "inactive")
              }
            >
              <option value="all">Tous</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div
              className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3 w-100"
              style={{ borderTopRightRadius: "8px" }}
            >
              <CitiesSvg className="fill-white" />
              <p className="fs-5 fw-bold m-0 text-white">
                {Cities?.length} {Cities?.length > 1 ? "Villes" : "Ville"}
              </p>
            </div>
          </div>
        </div>

        <RenderTable
          loading={fetchingCities}
          render={() =>
            paginatedContent?.length > 0 ? (
              <CityTable
                Cities={paginatedContent as any[]}
                CitiesToBeDeletedList={dispatch}
              />
            ) : (
              <p className="text-center fs-5 my-2">{general.fr.noData}</p>
            )
          }
        />
      </BoxWrapper>

      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
        siblingCount={1}
        totalCount={totalPages}
        pageSize={PAGE_SIZE}
      />
    </AdminRoute>
  );
}
